import { Routes, Route, BrowserRouter, Outlet } from 'react-router-dom';
import './App.css';

// Layouts
import DashboardLayout from '@/components/layout/DashboardLayout';
import { PublicLayout } from '@/components/layout/PublicLayout';

// Pages
import Login from '@/pages/Login';
import ResetPassword from '@/pages/ResetPassword';
import UpdatePassword from '@/pages/UpdatePassword';
import NotFound from '@/pages/NotFound';
import Dashboard from '@/pages/Dashboard';

// Public pages
import Index from '@/pages/Index';
import PublicItineraries from '@/pages/public/PublicItineraries';
import PublicItineraryDetails from '@/pages/public/PublicItineraryDetails';
import PublicBlogListing from '@/pages/public/PublicBlogListing';
import PublicBlogPost from '@/pages/public/PublicBlogPost';
import PremiumItineraries from '@/pages/public/PremiumItineraries';
import PremiumItineraryDetails from '@/pages/public/PremiumItineraryDetails';
import PurchaseSuccess from '@/pages/public/PurchaseSuccess';
import PublicDestinations from '@/pages/public/PublicDestinations';
import ContactUs from '@/pages/public/ContactUs';
import ProductsPage from '@/pages/public/ProductsPage';
import TravelNewsPage from '@/pages/public/TravelNewsPage';
import ShortContentPage from '@/pages/public/ShortContentPage';
import TravelResourcesPage from '@/pages/public/TravelResourcesPage';
import ShortContentDetail from '@/pages/public/ShortContentDetail';
import FlightSearch from '@/pages/public/FlightSearch';
import PrivacyPolicy from '@/pages/public/PrivacyPolicy';
import CookiePolicy from '@/pages/public/CookiePolicy';
import DealsPage from '@/pages/public/DealsPage';
import TravelQuiz from '@/pages/public/TravelQuiz';
import Settings from '@/pages/Settings';
import NoSignupPage from '@/pages/NoSignupPage';
import UnifiedAnalytics from '@/pages/UnifiedAnalytics';
import DealsManagement from '@/pages/DealsManagement';
import SocialCardsPage from '@/pages/SocialCards';
import QuizAnalytics from '@/pages/QuizAnalytics';
import EmailSignups from '@/pages/EmailSignups';

// China ecosystem pages
import ChinaHub from '@/pages/public/china/ChinaHub';
import ChinaListing from '@/pages/public/china/ChinaListing';

// Components
import { ScrollToTop } from '@/components/ui/scroll-to-top';

// Context providers
import { AuthProvider } from '@/contexts/AuthContext';
import { Toaster } from 'sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import AuthGuard from '@/components/auth/AuthGuard';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <NotificationsProvider>
            <Routes>
              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/update-password" element={<UpdatePassword />} />
              
              {/* No Signup Page - Shows banner explaining no public signup is available */}
              <Route path="/signup" element={<NoSignupPage />} />
              
              {/* Dashboard Routes - ALL protected by AuthGuard */}
              <Route path="/dashboard" element={
                <AuthGuard>
                  <DashboardLayout>
                    <Outlet />
                  </DashboardLayout>
                </AuthGuard>
              }>
                <Route index element={<Dashboard />} />
              <Route path="settings" element={<Settings />} />
                <Route path="analytics" element={<UnifiedAnalytics />} />
                <Route path="analytics/affiliates" element={<UnifiedAnalytics />} />
                <Route path="quiz-analytics" element={<QuizAnalytics />} />
                <Route path="deals" element={<DealsManagement />} />
                <Route path="social-cards" element={<SocialCardsPage />} />
                <Route path="email-signups" element={<EmailSignups />} />
                <Route path="*" element={<Dashboard />} />
              </Route>
              
              {/* Public Routes - Wrapped with PublicLayout */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Index />} />
                
                {/* China Ecosystem Routes - BEFORE wildcards for priority */}
                <Route path="/china" element={<ChinaHub />} />
                <Route path="/china/planning" element={<ChinaListing type="planning" />} />
                <Route path="/china/itineraries" element={<ChinaListing type="itinerary" />} />
                <Route path="/china/cities" element={<ChinaListing type="city" />} />
                <Route path="/china/resources" element={<ChinaListing type="resource" />} />
                
                <Route path="/flights" element={<FlightSearch />} />
                <Route path="/itineraries" element={<PublicItineraries />} />
                <Route path="/itineraries/:slug" element={<PublicItineraryDetails />} />
                <Route path="/blog" element={<PublicBlogListing />} />
                <Route path="/blog/:slug" element={<PublicBlogPost />} />
                <Route path="/premium-itineraries" element={<PremiumItineraries />} />
                <Route path="/premium-itineraries/:slug" element={<PremiumItineraryDetails />} />
                <Route path="/purchase-success" element={<PurchaseSuccess />} />
                <Route path="/destinations" element={<PublicDestinations />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/shop" element={<ProductsPage />} />
                <Route path="/travel-resources" element={<TravelResourcesPage />} />
                <Route path="/travel-news" element={<TravelNewsPage />} />
                <Route path="/travel-bytes" element={<ShortContentPage />} />
                <Route path="/travel-bytes/:slug" element={<ShortContentDetail />} />
                <Route path="/deals" element={<DealsPage />} />
                <Route path="/travel-quiz" element={<TravelQuiz />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
              </Route>
              
              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster richColors position="top-right" />
          </NotificationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
